import React, { FC, useEffect, useState } from "react"
import Loading from "../../../../ui/Loading"
import { KTitleSpan, KButton, KSpan, KTooltip, KInput } from "kahuna-base-react-components"
import { systems } from "../../../../../apis"
import { Setting } from "../../../../../types"
import { toast } from "react-toastify"
import { errorFilledToastOptions, successFilledToastOptions } from "../../../../../constants"

type SettingsProps = {
  setOpenSidebar: (openSidebar: boolean) => void
  openSidebar: boolean
}

const Settings: FC<SettingsProps> = (props) => {
  const [loading, setLoading] = useState(false)
  const [settingList, setSettingList] = useState<Setting[]>([])
  const [changedIdList, setChangedIdList] = useState<number[]>([])

  useEffect(() => {
    setLoading(true)
    systems(`/setting/`, "GET")
      .then((resp) => {
        setLoading(false)
        setSettingList(
          resp.data.results.map((setting: Setting) => {
            if (setting.type === "ci_token") {
              setting.value = ""
            }
            return setting
          })
        )
      })
      .catch(() => setLoading(false))
  }, [])

  const onValueChange = (value: string, settingId: number) => {
    changedIdList.push(settingId)
    setChangedIdList([...new Set(changedIdList)])

    const updatedList: Setting[] = []
    settingList.forEach((setting) => {
      if (setting.id === settingId) {
        setting.value = value
      }
      updatedList.push(setting)
    })
    setSettingList([...updatedList])
  }

  const onSave = () => {
    const updatedList = settingList.filter((setting: Setting) => changedIdList.includes(setting.id))
    setLoading(true)

    systems(`/setting/save/`, "PUT", updatedList)
      .then((resp) => {
        setLoading(false)
        if (resp.data.success) {
          toast.success(`Successfully Saved Setting`, successFilledToastOptions)
          setChangedIdList([])
        } else {
          toast.error(`Error saving Setting`, errorFilledToastOptions)
        }
      })
      .catch(() => setLoading(false))
  }

  const renderGroupedSettings = () => {
    // Group settings by their group
    const groupedSettings = settingList.reduce<Record<string, Setting[]>>((acc, setting) => {
      if (!acc[setting.group]) {
        acc[setting.group] = []
      }
      acc[setting.group].push(setting)
      return acc
    }, {})

    return Object.entries(groupedSettings).map(([groupName, settings]) => {
      return (
        <div className="mb-4 mt-4">
          <div style={{ borderBottom: "1px solid black" }}>
            <KTitleSpan fontSize={18} lineHeight="auto" text={groupName} />
          </div>
          <div>
            {settings.map((setting) => {
              return (
                <div className={"border-b-2 flex p-2"} style={{ width: "100%" }} key={setting.id}>
                  <div style={{ width: "22%" }}>
                    <KSpan text={setting.name} />
                  </div>
                  <div style={{ width: "43%", display: "grid" }}>
                    {setting.description.split("<br/>").map((desc) => {
                      return <KSpan key={Math.random()} text={desc} />
                    })}
                  </div>
                  <div style={{ width: "15%" }}>
                    <KSpan text={setting.updated?.substring(0, 10) || "-"} />
                  </div>
                  <div style={{ width: "20%" }}>
                    <KInput
                      data-testid={`setting-input-${setting.id}`}
                      value={setting.value}
                      onChange={(text) => onValueChange(text, setting.id)}
                    />
                  </div>
                </div>
              )
            })}
          </div>
        </div>
      )
    })
  }

  return (
    <div className="catalog flex flex-col bg-[#fff] px-8" style={{ paddingTop: 22 }}>
      <div className="flex justify-between items-center mb-6">
        <KTooltip
          backgroundColor="#000"
          padding="0px 4px"
          height="20px"
          arrowColor="#000"
          position="bottom"
          content={
            <div className="h-5 w-max flex items-center">
              <KSpan
                text={props.openSidebar ? "Hide sidebar" : "Open sidebar"}
                fontSize={12}
                lineHeight="16px"
                color="#FFF"
              />
            </div>
          }
        >
          <div className="flex items-center">
            <KButton
              dataTestId="reports-sidebar-toggle-button"
              icon="/left-sidebar.svg"
              width="32px"
              height="32px"
              background="transparent"
              shadowDisabled={true}
              padding="6px"
              onClick={() => props.setOpenSidebar(!props.openSidebar)}
            />
            <KTitleSpan fontSize={20} lineHeight="28px" text={"Settings"} />
          </div>
        </KTooltip>
      </div>
      <div className="w-[100%] p-4">
        {loading && <Loading />}
        <div className="mb-4">
          <div className={"border-b-2 flex"} style={{ width: "100%" }}>
            <div style={{ width: "22%" }}>
              <KSpan text="Name" color="#111" fontWeight={600} />
            </div>
            <div style={{ width: "43%" }}>
              <KSpan text="Description" color="#111" fontWeight={600} />
            </div>
            <div style={{ width: "15%" }}>
              <KSpan text="Updated" color="#111" fontWeight={600} />
            </div>
            <div style={{ width: "20%" }}>
              <KSpan text="Value" color="#111" fontWeight={600} />
            </div>
          </div>
          {renderGroupedSettings()}
        </div>
        <div className="flex justify-end">
          <KButton
            width="120px"
            data-testid="system-settings-save-button"
            disabled={changedIdList.length === 0}
            onClick={() => onSave()}
            text="Save"
          />
        </div>
      </div>
    </div>
  )
}

export default Settings
